import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { lowerCase, upperFirst } from 'lodash';
import { stringifyUrl } from 'query-string';

import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';

import { dateRanges } from '@dashboard/scenes/Dashboard/date-ranges';
import { METRICS } from '@dashboard/scenes/Dashboard/metrics';

import Button from '@dashboard/components/Button';
import { Label, Select } from '@dashboard/components/Form';

export default function AdminActions() {
  const { token } = useAuth();
  const { emitToast } = useToast();

  const [statisticsRange, setStatisticsRange] = React.useState('lastHour');
  const [metric, setMetric] = React.useState('');

  const startStatisticsAggregate = useMutation({
    mutationFn: () =>
      fetcher(
        stringifyUrl({
          url: `/admin/aggregate-historical-statistics`,
          query: {
            range: statisticsRange,
            ...(metric && { metric }),
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      emitToast({
        title: 'Statistics aggregation started',
        type: 'success',
      });
    },
    onError: error => {
      emitToast({
        title: 'Error starting statistics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const [metricsRange, setMetricsRange] = React.useState('lastHour');

  const startMetricsAggregation = useMutation({
    mutationFn: () =>
      fetcher(
        stringifyUrl({
          url: `/admin/aggregate-historical-metrics`,
          query: {
            range: metricsRange,
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      emitToast({
        title: 'Metrics aggregation started',
        type: 'success',
      });
    },
    onError: error => {
      emitToast({
        title: 'Error starting metrics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  return (
    <div className="">
      <div className="mb-4">
        <h1>Actions</h1>
      </div>

      <div className="mt-4">
        <Label>Aggregate historical statistics</Label>
        <div className="flex gap-x-2">
          <div>
            <Select onChange={e => setStatisticsRange(e.target.value)}>
              <option disabled>Range</option>
              <option value="lastHour" selected={statisticsRange === 'lastHour'}>
                Last hour
              </option>

              {Object.keys(dateRanges).map(range => (
                <option value={range} selected={range === statisticsRange} key={range}>
                  {dateRanges[range]?.label}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Select onChange={e => setMetric(e.target.value)}>
              <option disabled>Metric</option>
              <option value="" selected={metric === ''}>
                All metrics
              </option>
              {Object.entries(METRICS).map(([key, value]) => (
                <option value={value} selected={value === metric} key={value}>
                  {upperFirst(lowerCase(key))}
                </option>
              ))}
            </Select>
          </div>

          <Button onClick={() => startStatisticsAggregate.mutate()} appearance="primary">
            Start statistics aggregation for all sellers
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <Label>Aggregate historical metrics</Label>
        <div className="flex gap-x-2">
          <div>
            <Select onChange={e => setMetricsRange(e.target.value)}>
              <option disabled>Range</option>
              <option value="lastHour" selected={metricsRange === 'lastHour'}>
                Last hour
              </option>

              {Object.keys(dateRanges).map(range => (
                <option value={range} selected={range === metricsRange} key={range}>
                  {dateRanges[range]?.label}
                </option>
              ))}
            </Select>
          </div>

          <Button onClick={() => startMetricsAggregation.mutate()} appearance="primary">
            Start metrics aggregation for all sellers
          </Button>
        </div>
      </div>
    </div>
  );
}
